// src/modulos/Parametros/Tipos/TypeBoxPage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useSubCollection from '../../../hooks/useSubCollection';
import usePagePermissions from '../../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../../components/common';

// Import utilities
import { getTimestamps } from '../../../utils/timestamps';

// Page-specific styles
import styles from '../Parameters.module.css';

// Constantes da Coleção
const BOX_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-caixa'
};

const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};

const ITEMS_PER_PAGE = 10;

// Define box size classifications only (no dimensions)
const BOX_SIZES = ['PP', 'P', 'M', 'G', 'GG'];

const TypeBoxPage = () => {
    const can = usePagePermissions('tipo_de_caixa');

    // Estados Locais
    const [editingBox, setEditingBox] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [selectedProductTypeId, setSelectedProductTypeId] = useState('');

    // NOVO CAMPO PARA CLASSIFICAÇÃO DA CAIXA
    const [selectedBoxSize, setSelectedBoxSize] = useState('');

    // DIMENSÕES (mantidas para input manual)
    const [boxHeight, setBoxHeight] = useState('');
    const [boxWidth, setBoxWidth] = useState('');
    const [boxLength, setBoxLength] = useState('');

    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hooks de Dados
    const {
        data: boxTypes,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument
    } = useSubCollection(
        BOX_TYPE_COLLECTION_PATH.parentCollection,
        BOX_TYPE_COLLECTION_PATH.parentId,
        BOX_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    const {
        data: productTypes,
        loading: productTypesLoading
    } = useSubCollection(
        PRODUCT_TYPE_COLLECTION_PATH.parentCollection,
        PRODUCT_TYPE_COLLECTION_PATH.parentId,
        PRODUCT_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    // Função para calcular o volume da caixa em cm³
    const calculateVolumeCm3 = useCallback((height, width, length) => {
        const h = parseFloat(height) || 0;
        const w = parseFloat(width) || 0;
        const l = parseFloat(length) || 0;
        return h * w * l;
    }, []);

    // Função para calcular o volume em m³ (divide por 1.000.000)
    const calculateVolumeM3 = useCallback((height, width, length) => {
        const volumeCm3 = calculateVolumeCm3(height, width, length);
        return volumeCm3 / 1000000;
    }, [calculateVolumeCm3]);

    const getProductTypeName = useCallback((productTypeId) => {
        const productType = productTypes?.find(pt => pt.id === productTypeId);
        return productType ? productType.name : 'Tipo não encontrado';
    }, [productTypes]);

    const filteredBoxes = useMemo(() => {
        if (!boxTypes) return [];

        let result = [...boxTypes];

        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            result = result.filter(box =>
                box.name.toLowerCase().includes(lowerCaseSearch) ||
                getProductTypeName(box.productTypeId).toLowerCase().includes(lowerCaseSearch) ||
                (box.boxSize && box.boxSize.toLowerCase().includes(lowerCaseSearch))
            );
        }

        result.sort((a, b) => a.name.localeCompare(b.name));
        return result;
    }, [boxTypes, searchTerm, getProductTypeName]);

    const totalFilteredItems = filteredBoxes.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentBoxes = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredBoxes.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredBoxes, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const resetForm = useCallback(() => {
        setEditingBox(null);
        setNameInput('');
        setSelectedProductTypeId('');
        setSelectedBoxSize('');
        setBoxHeight('');
        setBoxWidth('');
        setBoxLength('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((box) => {
        setEditingBox(box);
        setNameInput(box.name);
        setSelectedProductTypeId(box.productTypeId || '');
        setSelectedBoxSize(box.boxSize || '');
        setBoxHeight(box.height?.toString() || box.boxHeight?.toString() || '');
        setBoxWidth(box.width?.toString() || box.boxWidth?.toString() || '');
        setBoxLength(box.length?.toString() || box.boxLength?.toString() || '');
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Tipo de Caixa "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingItems = filteredBoxes.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError(`Erro ao deletar tipo de caixa: ${result.error}`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar tipo de caixa. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredBoxes, currentPage]);

    const validateForm = useCallback(() => {
        if (!nameInput.trim()) {
            setFormError("O nome do tipo de caixa é obrigatório.");
            return false;
        }

        if (!selectedProductTypeId) {
            setFormError("Selecione um tipo de produto.");
            return false;
        }

        if (!selectedBoxSize) {
            setFormError("Selecione a classificação da caixa.");
            return false;
        }

        // Validação das dimensões (opcional, mas recomenda-se)
        if (boxHeight && parseFloat(boxHeight) <= 0) {
            setFormError("A altura deve ser um valor positivo.");
            return false;
        }
        if (boxWidth && parseFloat(boxWidth) <= 0) {
            setFormError("A largura deve ser um valor positivo.");
            return false;
        }
        if (boxLength && parseFloat(boxLength) <= 0) {
            setFormError("O comprimento deve ser um valor positivo.");
            return false;
        }

        return true;
    }, [nameInput, selectedProductTypeId, selectedBoxSize, boxHeight, boxWidth, boxLength]);

    // Calcular volume em tempo real para exibição no formulário (em m³)
    const currentVolumeM3 = useMemo(() => {
        return calculateVolumeM3(boxHeight, boxWidth, boxLength);
    }, [boxHeight, boxWidth, boxLength, calculateVolumeM3]);

    // Calcular volume em cm³ para salvar no banco (opcional - manter para referência)
    const currentVolumeCm3 = useMemo(() => {
        return calculateVolumeCm3(boxHeight, boxWidth, boxLength);
    }, [boxHeight, boxWidth, boxLength, calculateVolumeCm3]);

    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!validateForm()) return;

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingBox, userEmail);
        const data = {
            name: nameInput.trim(),
            productTypeId: selectedProductTypeId,
            boxSize: selectedBoxSize, // Classificação PP, P, M, G, GG
            // Dimensões da caixa (em cm)
            height: boxHeight ? parseFloat(boxHeight) : 0,
            width: boxWidth ? parseFloat(boxWidth) : 0,
            length: boxLength ? parseFloat(boxLength) : 0,
            // Volume em m³ (salvo no banco)
            volumeM3: currentVolumeM3,
            // Volume em cm³ (opcional - pode ser útil para referência)
            volumeCm3: currentVolumeCm3,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingBox) {
                result = await updateDocument(editingBox.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingBox ? 'Tipo de Caixa editado com sucesso!' : 'Tipo de Caixa adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingBox ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            console.error("Erro ao salvar tipo de caixa:", error);
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, selectedProductTypeId, selectedBoxSize, boxHeight, boxWidth, boxLength, currentVolumeM3, currentVolumeCm3, editingBox, validateForm, addDocument, updateDocument, handleCloseModal]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingBox ? `Editar: ${editingBox.name}` : 'Adicionar Novo Tipo de Caixa'}
                subtitle={editingBox ? `Editando: ${editingBox.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingBox ? '💾 Salvar Edição' : '✨ Adicionar Caixa'}
                size="large"
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome do Tipo de Caixa <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Caixa Pequena, Caixa Média, Caixa Grande, Fardo"
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Nome descritivo para identificar este tipo de embalagem
                    </small>
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="productType">
                        Tipo de Produto <span className={styles.required}>*</span>
                    </label>
                    <select
                        id="productType"
                        value={selectedProductTypeId}
                        onChange={(e) => setSelectedProductTypeId(e.target.value)}
                        className={styles.select}
                        required
                        disabled={formLoading || productTypesLoading}
                    >
                        <option value="">Selecione um tipo de produto...</option>
                        {productTypes?.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                    <small className={styles.fieldHint}>
                        Define quais produtos podem ser embalados nesta caixa
                    </small>
                </div>

                {/* NOVO DROPDOWN PARA CLASSIFICAÇÃO DA CAIXA */}
                <div className={styles.inputGroup}>
                    <label htmlFor="boxSize">
                        Classificação da Caixa <span className={styles.required}>*</span>
                    </label>
                    <select
                        id="boxSize"
                        value={selectedBoxSize}
                        onChange={(e) => setSelectedBoxSize(e.target.value)}
                        className={styles.select}
                        required
                        disabled={formLoading}
                    >
                        <option value="">Selecione a classificação...</option>
                        {BOX_SIZES.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                    <small className={styles.fieldHint}>
                        Classificação do tamanho da caixa: PP (Muito Pequeno), P (Pequeno), M (Médio), G (Grande), GG (Muito Grande)
                    </small>
                </div>

                {/* CAMPOS DE DIMENSÕES - Layout em Grid */}
                <div className={styles.formGrid} style={{ marginTop: '16px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="boxHeight">
                            Altura da Caixa (cm)
                        </label>
                        <input
                            id="boxHeight"
                            type="number"
                            value={boxHeight}
                            onChange={(e) => setBoxHeight(e.target.value)}
                            className={styles.input}
                            placeholder="Ex: 20"
                            min="0"
                            step="0.1"
                            disabled={formLoading}
                        />
                        <small className={styles.fieldHint}>
                            Altura individual da caixa
                        </small>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="boxWidth">
                            Largura da Caixa (cm)
                        </label>
                        <input
                            id="boxWidth"
                            type="number"
                            value={boxWidth}
                            onChange={(e) => setBoxWidth(e.target.value)}
                            className={styles.input}
                            placeholder="Ex: 30"
                            min="0"
                            step="0.1"
                            disabled={formLoading}
                        />
                        <small className={styles.fieldHint}>
                            Largura individual da caixa
                        </small>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="boxLength">
                            Comprimento da Caixa (cm)
                        </label>
                        <input
                            id="boxLength"
                            type="number"
                            value={boxLength}
                            onChange={(e) => setBoxLength(e.target.value)}
                            className={styles.input}
                            placeholder="Ex: 40"
                            min="0"
                            step="0.1"
                            disabled={formLoading}
                        />
                        <small className={styles.fieldHint}>
                            Comprimento individual da caixa
                        </small>
                    </div>
                </div>

                {/* Display Calculated Volume em m³ */}
                {currentVolumeM3 > 0 && (
                    <div className={styles.volumeDisplay} style={{
                        marginTop: '16px',
                        padding: '12px 16px',
                        backgroundColor: '#f0f7ff',
                        borderRadius: '6px',
                        border: '1px solid #d0e4ff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{ fontWeight: '500', color: '#1a3a6b' }}>
                            📦 Volume calculado:
                        </span>
                        <span style={{
                            fontWeight: 'bold',
                            fontSize: '1.1em',
                            color: '#0066cc'
                        }}>
                            {currentVolumeM3.toFixed(6)} m³
                            {currentVolumeM3 < 0.001 && currentVolumeM3 > 0 && (
                                <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '8px', color: '#666' }}>
                                    ({currentVolumeM3 * 1000} L)
                                </span>
                            )}
                            {currentVolumeM3 >= 0.001 && (
                                <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '8px', color: '#666' }}>
                                    ({currentVolumeM3.toFixed(3)} m³)
                                </span>
                            )}
                        </span>
                    </div>
                )}
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja excluir o tipo de caixa "${deleteConfirm.name}"?\n\nEsta ação não pode ser desfeita e pode afetar registros que utilizam esta caixa.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Tipos de Caixa"
                actionText="Tipo de Caixa"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome da caixa, classificação ou tipo de produto..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
                {searchTerm && (
                    <div className={styles.searchInfo}>
                        Encontrados {filteredBoxes.length} resultado(s)
                    </div>
                )}
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar tipos de caixa: {listError}</p>
                    <button onClick={() => window.location.reload()} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={6} rows={ITEMS_PER_PAGE} />
                    ) : currentBoxes.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📦'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado para sua busca.'
                                    : 'Nenhum tipo de caixa cadastrado.'
                            }
                            message={
                                searchTerm
                                    ? 'Tente outros termos ou limpe a busca.'
                                    : "Clique em 'Adicionar Tipo de Caixa' para começar."
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome da Caixa</th>
                                            <th>Tipo de Produto</th>
                                            <th>Classificação</th>
                                            <th>Dimensões (L × C × A) cm</th>
                                            <th>Volume (m³)</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentBoxes.map((box) => {
                                            // Tenta usar volumeM3 salvo, se não existir, calcula
                                            let volumeM3 = box.volumeM3;
                                            if (volumeM3 === undefined || volumeM3 === null) {
                                                const height = box.height || box.boxHeight || 0;
                                                const width = box.width || box.boxWidth || 0;
                                                const length = box.length || box.boxLength || 0;
                                                volumeM3 = calculateVolumeM3(height, width, length);
                                            }

                                            return (
                                                <tr key={box.id}>
                                                    <td className={styles.conditionName}>
                                                        <strong>{box.name}</strong>
                                                    </td>
                                                    <td>
                                                        <span className={styles.typePhysicoChemical}>
                                                            {getProductTypeName(box.productTypeId)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            fontWeight: 'bold',
                                                            color: box.boxSize ? '#0066cc' : '#999'
                                                        }}>
                                                            {box.boxSize || '--'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {box.width || box.length || box.height ? (
                                                            <span>
                                                                {box.width || 0} × {box.length || 0} × {box.height || 0}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                                Não definidas
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {volumeM3 > 0 ? (
                                                            <span style={{ fontWeight: '500', color: '#0066cc' }}>
                                                                {volumeM3.toFixed(6)}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                                --
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className={styles.actionsCell}>
                                                        {can.edit && (
                                                            <button
                                                                onClick={() => handleEditClick(box)}
                                                                className={styles.actionButton}
                                                                disabled={formLoading}
                                                            >
                                                                Editar
                                                            </button>
                                                        )}
                                                        {can.delete && (
                                                            <button
                                                                onClick={() => handleDeleteClick(box.id, box.name)}
                                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                disabled={formLoading}
                                                            >
                                                                Deletar
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="tipos de caixa"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TypeBoxPage;